import { call } from '@redux-saga/core/effects';
import {
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  ModalType,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  convertVermittlernummerToString,
  getAppEnumBySpartenType,
  getBgvg,
} from '@ps20-frontend/frontend-models';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import { cancel, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  KontaflexFrontendRequestModel,
  KontaflexFrontendResponseModel,
  KontaflexInput,
  KontaflexTreuebonusDeckungError,
  SparteAntrag,
  Status,
  TaasNachlassVorteilRequest,
  Vermittlernummer,
  Vertragsgrund,
} from '@ps20/bff-models';
import { AnyAction } from 'redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { CheckoutActions } from '../checkout.actions';
import { getVorschlagsdaten } from './vorschlagsdatenHelper';

export enum KONTAFLEX_REQUEST_EVENTS {
  KONTAFLEX_REQUEST_FOR_EINREICHEN_SUCCESSFUL = 'KONTAFLEX_REQUEST_FOR_EINREICHEN_SUCCESSFUL',
  KONTAFLEX_REQUEST_FOR_CAP_SUCCESSFUL = 'KONTAFLEX_REQUEST_FOR_CAP_SUCCESSFUL',
  KONTAFLEX_REQUEST_FOR_EINREICHEN_ERROR = 'KONTAFLEX_REQUEST_FOR_EINREICHEN_ERROR',
  KONTAFLEX_REQUEST_FOR_CAP_ERROR = 'KONTAFLEX_REQUEST_FOR_CAP_ERROR',
}

export const URL_KONTAFLEX = '/privatschutz/api/kontaflex';
export const URL_AGENCY_ACCOUNT_INFO = '/privatschutz/api/agencyAccountInfo';

export function* watchKontaflex() {
  yield takeLatest(
    CheckoutActions.EINREICHEN_BUTTON_CLICKED,
    safeSaga(doKontaflexRequest, App.SHOP_CHECKOUT),
  );
  yield takeLatest(CheckoutActions.CAP_START, safeSaga(doKontaflexRequest, App.SHOP_CHECKOUT));
  yield takeEvery(
    KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_EINREICHEN_SUCCESSFUL,
    safeSaga(successCallbackForEinreichen, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_CAP_SUCCESSFUL,
    safeSaga(successCallbackForCap, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_EINREICHEN_ERROR,
    safeSaga(errorCallbackForEinreichen, App.SHOP_CHECKOUT),
  );
  yield takeEvery(
    KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_CAP_ERROR,
    safeSaga(errorCallbackForCap, App.SHOP_CHECKOUT),
  );
}

export function* doKontaflexRequest(action: AnyAction) {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const selectedVermittlernummer: Vermittlernummer = state.vermittler.fremd
    ? state.vermittler.selectedVermittlernummer.fremd
    : state.vermittler.selectedVermittlernummer.eigen;
  if (!convertVermittlernummerToString(selectedVermittlernummer)) {
    yield triggerEinreichen(globalPut, action.type);
    yield cancel();
  }
  const vorschlagsdaten: Array<SparteAntrag> = yield getVorschlagsdaten();

  const kontaflexBody: KontaflexFrontendRequestModel = {
    bgvg: getBgvg(selectedVermittlernummer.zweigniederlassung),
    sammelnummer:
      selectedVermittlernummer.geschaeftsstelle + selectedVermittlernummer.vertreternummer,
    kontaflexInputList: [],
  };
  const hasAnyVorteilsrabatt = vorschlagsdaten.some(
    v => v.geschaeftsart !== Vertragsgrund.BEIBEHALTUNG && v.vorteilsrabatt > 0,
  );

  vorschlagsdaten.forEach(vorschlagsdata => {
    if (vorschlagsdata.geschaeftsart === Vertragsgrund.BEIBEHALTUNG) {
      return;
    }
    const hasOwnKontaflexInput =
      vorschlagsdata.treuebonus > 0 || vorschlagsdata.vorteilsrabatt > 0;
    if (!hasOwnKontaflexInput && !hasAnyVorteilsrabatt) {
      return;
    }
    const vorteilsrabattBelastungInput = state.produkte.find(
      produkt =>
        produkt.angebotsId === vorschlagsdata.angebotsId &&
        produkt.appId === getAppEnumBySpartenType(vorschlagsdata.spartenType),
    )?.vorteilsrabattBelastungInput;
    const taasNachlassVorteilRequest: TaasNachlassVorteilRequest = vorteilsrabattBelastungInput
      ? {
          angebotsId: vorschlagsdata.angebotsId,
          nachlass: vorschlagsdata.vorteilsrabatt,
          spartenType: vorschlagsdata.spartenType,
          belastfakHoch: vorteilsrabattBelastungInput.taasNachlassVorteil.belastfakHoch,
          kipp: vorteilsrabattBelastungInput.taasNachlassVorteil.kipp,
          startgutschriftPrz:
            vorteilsrabattBelastungInput.taasNachlassVorteil.startgutschriftPrz,
          belastfakNiedrig: vorteilsrabattBelastungInput.taasNachlassVorteil.belastfakNiedrig,
          max: vorteilsrabattBelastungInput.taasNachlassVorteil.max,
          cpSternBausteine: vorteilsrabattBelastungInput.cpSternSum,
          jahresNettoBausteine: vorteilsrabattBelastungInput.jahresNettoBausteineSum,
        }
      : null;

    const kontaflexInput: KontaflexInput = {
      taasNachlassVorteilRequest,
      jahresbeitrag: vorschlagsdata.jahresbeitrag,
      treuebonusProzentsatz:
        vorschlagsdata.treuebonus > 0 ? vorschlagsdata.treuebonus : vorschlagsdata.vorteilsrabatt,
    };
    kontaflexBody.kontaflexInputList.push(kontaflexInput);
  });
  let url: string | null;
  if (kontaflexBody.kontaflexInputList.length === 0) {
    url = null;
  } else if (hasAnyVorteilsrabatt) {
    url = URL_AGENCY_ACCOUNT_INFO;
  } else {
    url = URL_KONTAFLEX;
  }
  if (url !== null) {
    const successEvent =
      action.type === CheckoutActions.EINREICHEN_BUTTON_CLICKED
        ? KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_EINREICHEN_SUCCESSFUL
        : KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_CAP_SUCCESSFUL;
    const errorEvent =
      action.type === CheckoutActions.EINREICHEN_BUTTON_CLICKED
        ? KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_EINREICHEN_ERROR
        : KONTAFLEX_REQUEST_EVENTS.KONTAFLEX_REQUEST_FOR_CAP_ERROR;
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().doRequest({
        appId: App.SHOP_CHECKOUT,
        url,
        body: kontaflexBody,
        noCache: true,
        successEvent,
        errorEvent,
      }),
    );
  } else {
    yield triggerEinreichen(globalPut, action.type);
  }
}


export function* successCallbackForEinreichen(
  response: DoRequestResponse<KontaflexFrontendResponseModel>,
) {
  const globalPut = GlobalPut.getInstance();
  if (
    response.payload?.status === Status.NOK ||
    response.payload?.serviceError === KontaflexTreuebonusDeckungError.TREUEBONUS_DECKUNG_ERROR
  ) {
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().requestModal(ModalType.TREUE_BONUS_KONTO_ERROR),
    );
  } else {
    yield call([globalPut, globalPut.put], new CheckoutActions().antragEinreichen());
  }
}

export function* successCallbackForCap(
  response: DoRequestResponse<KontaflexFrontendResponseModel>,
) {
  const globalPut = GlobalPut.getInstance();
  if (
    response.payload?.status === Status.NOK ||
    response.payload?.serviceError === KontaflexTreuebonusDeckungError.TREUEBONUS_DECKUNG_ERROR
  ) {
    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().requestModal(ModalType.TREUE_BONUS_KONTO_ERROR),
    );
  } else {
    yield call([globalPut, globalPut.put], new CheckoutActions().antragEinreichenCap());
  }
}

export function* errorCallbackForEinreichen() {
  const globalPut = GlobalPut.getInstance();
  yield call([globalPut, globalPut.put], new CheckoutActions().antragEinreichen());
}

export function* errorCallbackForCap() {
  const globalPut = GlobalPut.getInstance();
  yield call([globalPut, globalPut.put], new CheckoutActions().antragEinreichenCap());
}

export function* triggerEinreichen(globalPut: GlobalPut, actionType: string) {
  if (actionType === CheckoutActions.EINREICHEN_BUTTON_CLICKED) {
    yield call([globalPut, globalPut.put], new CheckoutActions().antragEinreichen());
  } else {
    yield call([globalPut, globalPut.put], new CheckoutActions().antragEinreichenCap());
  }
}
